import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAttendance } from '../context/AttendanceContext';

const {
  FiCalendar,
  FiUsers,
  FiClock,
  FiCheck,
  FiX,
  FiEdit2,
  FiTrash2,
  FiAlertCircle,
  FiCheckCircle,
  FiCircle,
  FiSquare,
  FiCheckSquare
} = FiIcons;

const MeetingHistoryManager = ({ onNavigate }) => {
  const {
    members,
    weeklyAttendance,
    addHistoricalMeeting,
    updateHistoricalMeeting,
    deleteHistoricalMeeting
  } = useAttendance();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [meetingDate, setMeetingDate] = useState('');
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [selectedLateAttendees, setSelectedLateAttendees] = useState([]);
  const [lateMinutes, setLateMinutes] = useState({});
  const [meetingError, setMeetingError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);

  const activeMembers = members.filter(m => m.active);
  const completedMeetings = weeklyAttendance.filter(m => !m.isActive)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMeetingError('');
    setIsSubmitting(true);

    try {
      if (!meetingDate) {
        setMeetingError('Please enter a valid meeting date');
        setIsSubmitting(false);
        return;
      }

      // Ensure we're using the local date as entered by the user without timezone adjustments
      let formattedDate = meetingDate;
      
      if (!editingMeeting) {
        // Check if a meeting already exists for this date
        const dateExists = weeklyAttendance.some(m => {
          // Compare the date strings directly to avoid timezone issues
          const existingDate = m.date.split('T')[0];
          return existingDate === formattedDate;
        });

        if (dateExists) {
          setMeetingError('A meeting already exists for this date');
          setIsSubmitting(false);
          return;
        }
      }

      // Create attendee data with the correct format
      const attendeesData = selectedAttendees.map(memberId => {
        const isLate = selectedLateAttendees.includes(memberId);
        return {
          memberId,
          checkInTime: new Date().toISOString(),
          minutesLate: isLate ? lateMinutes[memberId] || 0 : 0
        };
      });

      if (editingMeeting) {
        await updateHistoricalMeeting(editingMeeting.id, {
          date: formattedDate,
          attendees: attendeesData
        });
      } else {
        await addHistoricalMeeting({
          date: formattedDate,
          attendees: attendeesData
        });
      }

      resetForm();
    } catch (error) {
      console.error("Error saving meeting:", error);
      setMeetingError(`Failed to save meeting: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setEditingMeeting(null);
    setMeetingDate('');
    setSelectedAttendees([]);
    setSelectedLateAttendees([]);
    setLateMinutes({});
    setMeetingError('');
  };

  const handleEdit = (meeting) => {
    try {
      // Extract just the date part to prevent timezone issues
      let meetingDateStr = meeting.date;
      if (meetingDateStr.includes('T')) {
        meetingDateStr = meetingDateStr.split('T')[0];
      }
      
      setMeetingDate(meetingDateStr);
      setSelectedAttendees(meeting.attendees.map(a => a.memberId));
      setSelectedLateAttendees(meeting.attendees.filter(a => a.minutesLate > 0).map(a => a.memberId));
      
      const newLateMinutes = {};
      meeting.attendees.forEach(a => {
        if (a.minutesLate > 0) {
          newLateMinutes[a.memberId] = a.minutesLate;
        }
      });
      
      setLateMinutes(newLateMinutes);
      setEditingMeeting(meeting);
      setShowAddForm(true);
    } catch (error) {
      console.error("Error preparing edit form:", error);
      setMeetingError("Failed to load meeting data for editing");
    }
  };

  const handleDelete = async (meeting) => {
    try {
      await deleteHistoricalMeeting(meeting.id);
      setShowDeleteConfirm(false);
      setMeetingToDelete(null);
    } catch (error) {
      console.error("Error deleting meeting:", error);
      setMeetingError(`Failed to delete meeting: ${error.message}`);
    }
  };

  const toggleAttendee = (memberId) => {
    setSelectedAttendees(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );

    if (!selectedAttendees.includes(memberId)) {
      setSelectedLateAttendees(prev => prev.filter(id => id !== memberId));
      setLateMinutes(prev => {
        const { [memberId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const toggleLateAttendee = (memberId) => {
    if (!selectedAttendees.includes(memberId)) return;

    setSelectedLateAttendees(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );

    if (!lateMinutes[memberId]) {
      setLateMinutes(prev => ({
        ...prev,
        [memberId]: 5
      }));
    }
  };

  const updateLateMinutes = (memberId, minutes) => {
    setLateMinutes(prev => ({
      ...prev,
      [memberId]: parseInt(minutes) || 0
    }));
  };

  // Function to format dates correctly for display without timezone conversion
  const formatDisplayDate = (dateString) => {
    try {
      // Extract just the date part if it's an ISO string
      let dateOnly = dateString;
      if (dateString.includes('T')) {
        dateOnly = dateString.split('T')[0];
      }
      
      // Parse the date parts manually to avoid timezone issues
      const [year, month, day] = dateOnly.split('-').map(Number);
      
      // Create date object using local timezone
      const date = new Date(year, month - 1, day);
      
      // Format manually to avoid any timezone conversion
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      
      return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <h2 className="text-3xl font-bold text-gray-800">Meeting History</h2>
        <p className="text-gray-600 mt-2">
          View and manage historical meeting records
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-xl font-semibold flex items-center">
            <SafeIcon icon={FiCalendar} className="mr-3" />
            Meeting Records
          </h3>
        </div>
        <div className="card-body">
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary flex items-center mb-6"
          >
            <SafeIcon icon={FiCalendar} className="mr-2" />
            Add Historical Meeting
          </button>

          <AnimatePresence>
            {showAddForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h4 className="text-lg font-semibold mb-4">
                    {editingMeeting ? 'Edit Meeting' : 'Add Historical Meeting'}
                  </h4>

                  {meetingError && (
                    <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                      <SafeIcon icon={FiAlertCircle} className="mr-2" />
                      {meetingError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meeting Date
                      </label>
                      <input
                        type="date"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Attendees
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {activeMembers.map(member => (
                          <div
                            key={member.id}
                            className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                              selectedAttendees.includes(member.id)
                                ? 'bg-green-50 border-green-200'
                                : 'bg-gray-50 border-gray-200'
                            }`}
                            onClick={() => toggleAttendee(member.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{member.name}</p>
                                {member.company && (
                                  <p className="text-sm text-gray-600">{member.company}</p>
                                )}
                              </div>
                              <SafeIcon
                                icon={selectedAttendees.includes(member.id) ? FiCheckSquare : FiSquare}
                                className={selectedAttendees.includes(member.id) ? 'text-green-500' : 'text-gray-400'}
                              />
                            </div>

                            {selectedAttendees.includes(member.id) && (
                              <div className="mt-2">
                                <label className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedLateAttendees.includes(member.id)}
                                    onChange={() => toggleLateAttendee(member.id)}
                                    className="form-checkbox"
                                  />
                                  <span className="text-sm">Late arrival</span>
                                </label>

                                {selectedLateAttendees.includes(member.id) && (
                                  <div className="mt-2 flex items-center space-x-2">
                                    <input
                                      type="number"
                                      min="1"
                                      value={lateMinutes[member.id] || 5}
                                      onChange={(e) => updateLateMinutes(member.id, e.target.value)}
                                      className="w-20 px-2 py-1 border border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-600">minutes late</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="animate-spin mr-2">⟳</span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiCheck} className="mr-2" />
                            {editingMeeting ? 'Update Meeting' : 'Add Meeting'}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={resetForm}
                        className="btn-secondary"
                      >
                        <SafeIcon icon={FiX} className="mr-2" />
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-4">
            {completedMeetings.map((meeting, index) => (
              <motion.div
                key={meeting.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-lg border border-gray-200 p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold">
                      {formatDisplayDate(meeting.date)}
                    </h4>
                    <p className="text-gray-600">
                      {meeting.attendees.length} attendees
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(meeting)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                    >
                      <SafeIcon icon={FiEdit2} />
                    </button>
                    <button
                      onClick={() => {
                        setMeetingToDelete(meeting);
                        setShowDeleteConfirm(true);
                      }}
                      className="p-2 text-red-600 hover:bg-red-100 rounded"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {meeting.attendees.map(attendee => {
                    const member = members.find(m => m.id === attendee.memberId);
                    if (!member) return null;

                    return (
                      <div
                        key={attendee.memberId}
                        className={`p-3 rounded ${
                          attendee.minutesLate > 0
                            ? 'bg-orange-50'
                            : 'bg-green-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{member.name}</p>
                            {attendee.minutesLate > 0 && (
                              <p className="text-sm text-orange-600">
                                {attendee.minutesLate} minutes late
                              </p>
                            )}
                          </div>
                          <SafeIcon
                            icon={attendee.minutesLate > 0 ? FiClock : FiCheck}
                            className={
                              attendee.minutesLate > 0
                                ? 'text-orange-500'
                                : 'text-green-500'
                            }
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}

            {completedMeetings.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <SafeIcon icon={FiCalendar} className="mx-auto text-4xl mb-4" />
                <p>No meeting records found</p>
                <p className="text-sm">Add a historical meeting to get started</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showDeleteConfirm && meetingToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-red-600 mb-4">
                Delete Meeting Record
              </h3>
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the meeting record for{' '}
                {formatDisplayDate(meetingToDelete.date)}?
                This action cannot be undone.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => handleDelete(meetingToDelete)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                >
                  Delete Meeting
                </button>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setMeetingToDelete(null);
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MeetingHistoryManager;